import { HandRaiseIcon, MinusIcon, PlusIcon } from "@minervaproject/fds";
import { throttle } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";

import { REACTION_INFO_MAP } from "components/shared/signals_and_reactions";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { USER_PICKER_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { setUserReaction, toggleUserHandRaised } from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { ReactionType, RoleType, UserType } from "shared/types/user";
import { FilterStrategyType } from "shared/utils/user_picker";
import { classroomSelectors, localSelectors } from "store";
import { pickUsersAndSetParticipantBarAndStage } from "utils/classroom";

const REACTION_TO_STRATEGY_MAP = {
  like: FilterStrategyType.RecentlyLiked,
  dislike: FilterStrategyType.RecentlyDisliked,
  agree: FilterStrategyType.RecentlyAgreed,
  disagree: FilterStrategyType.RecentlyDisagreed,
  laugh: FilterStrategyType.RecentlyLaughed,
  wow: FilterStrategyType.RecentlyWowed,
  love: FilterStrategyType.RecentlyLoved,
  clap: FilterStrategyType.RecentlyClapped,
};

function ReactionCounter({
  reaction,
  count,
  canControlClassroom,
  canFilter,
  canEmote,
  currentUser,
  handleClick,
  className = "",
}: {
  reaction: string;
  count: number;
  canControlClassroom: boolean;
  canFilter: boolean;
  canEmote: boolean;
  currentUser: UserType;
  handleClick?: () => void;
  className?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  // Prevent hover state from sticking after clicking
  const isHoveredRef = useRef(false);
  const buttonElem = document.querySelector(`.reaction-counter-button.${reaction}`);
  isHoveredRef.current = !!(canEmote && buttonElem?.matches(":hover"));
  if (isHoveredRef.current !== isHovered) {
    setIsHovered(isHoveredRef.current);
  }

  const currUserHasThisReaction =
    (reaction === "hand" && currentUser.is_hand_raised) || reaction === currentUser.reaction;

  let bgColor;
  let ReactionIcon;
  if (reaction === "hand") {
    bgColor = "bg-yellow-green-tint-20";
    ReactionIcon = HandRaiseIcon;
  } else {
    const { color, color2 } = REACTION_INFO_MAP[reaction];
    bgColor = color2 ? `bg-${color2}` : `bg-${color}-tint-20`;
    ReactionIcon = REACTION_INFO_MAP[reaction].ReactionIcon;
  }

  let label;
  if (canFilter) {
    label = `Filter ${reaction}`;
  } else if (canEmote) {
    if (currUserHasThisReaction) {
      label = reaction === "hand" ? "Lower hand" : `Remove ${reaction}`;
    } else {
      label = reaction === "hand" ? "Raise hand" : `Emote ${reaction}`;
    }
  }
  const getOpacity = () => {
    if (canControlClassroom && (currUserHasThisReaction || count > 0)) return 1;
    if (canEmote && currUserHasThisReaction) return 1;
    return 0.35;
  };

  const throttledHandleHover = useMemo(
    () => throttle(() => setIsHovered(true), 50, { leading: false }),
    [],
  );

  return (
    <div className={`flex flex-column items-center ${className}`}>
      <div
        onMouseEnter={canEmote ? throttledHandleHover : undefined}
        onMouseLeave={canEmote ? () => setIsHovered(false) : undefined}
      >
        {isHovered ? (
          <button
            className={`reaction-counter-button ${reaction} button-reset cursor-pointer flex`}
            type="button"
            aria-label={label}
            onClick={handleClick}
          >
            {currUserHasThisReaction ? (
              <MinusIcon
                width={22}
                height={22}
                className={`minus ${bgColor}`}
                color="white"
                style={{
                  borderRadius: "50%",
                  padding: "5px",
                }}
              />
            ) : (
              <PlusIcon
                width={22}
                height={22}
                className={`plus ${bgColor}`}
                color="white"
                style={{
                  borderRadius: "50%",
                  padding: "5px",
                }}
              />
            )}
          </button>
        ) : canEmote || canFilter ? (
          <button
            className={`reaction-counter-button ${reaction} reaction-icon button-reset cursor-pointer flex`}
            type="button"
            aria-label={label}
            onClick={handleClick}
          >
            <ReactionIcon width={32} height={32} style={{ opacity: getOpacity() }} />
          </button>
        ) : (
          <span className="flex" data-testid={`reaction-counter-${reaction}-icon`}>
            <ReactionIcon width={32} height={32} style={{ opacity: getOpacity() }} />
          </span>
        )}
      </div>
      <span className="body-s text-black-tint-70 m1" data-testid={`${reaction}-count`}>
        {count}
      </span>
    </div>
  );
}

function ReactionRollup({ canFilter }: { canFilter: boolean }) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const users = useUsersProperties(USER_PICKER_PROPERTIES.concat(["is_hand_raised", "reaction"]));
  const presentStudents = Object.values(users).filter(
    ({ id, is_present }) => canParticipateInClassUserIds.includes(id) && is_present,
  );

  // For filtering
  const maxSubscribedStreams = useAppSelector(classroomSelectors.selectMaxSubscribedStreams());
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const presentVisibleUsers = Object.values(users).filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  // All visible users that can't control can click emote/raise hand but only students will move the counters
  const canEmote = !canControlClassroom && isVisibleInClassUserIds.includes(currentUserId);

  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());

  const getReactionCount = useCallback(
    (reaction: string) => {
      return presentStudents.filter(
        (user) => user.role === RoleType.Student && user.reaction === reaction,
      ).length;
    },
    [presentStudents],
  );

  const getHandRaiseCount = useCallback(() => {
    return presentStudents.filter((user) => user.role === RoleType.Student && user.is_hand_raised)
      .length;
  }, [presentStudents]);

  const handleClickReaction = (reaction: string) => {
    if (canFilter) {
      performFilter(reaction);
    } else if (canEmote) {
      dispatch(setUserReaction({ userId: currentUserId, reaction: reaction as ReactionType }));
    }
  };

  const handleClickHandRaise = () => {
    if (canFilter) {
      performFilter("hand");
    } else if (canEmote) {
      dispatch(toggleUserHandRaised({ userId: currentUserId }));
    }
  };

  const performFilter = (reactionOrHand: string) => {
    pickUsersAndSetParticipantBarAndStage(
      dispatch,
      maxSubscribedStreams,
      currentUserId,
      presentVisibleUsers,
      isAlwaysSubscribedToUserIds,
      canParticipateInClassUserIds,
      participantBarUserIds,
      panes,
      reactionOrHand === "hand"
        ? FilterStrategyType.RaisedHand
        : REACTION_TO_STRATEGY_MAP[reactionOrHand as Exclude<ReactionType, null>],
    );
  };

  return (
    <>
      <div className="flex justify-center">
        {["like", "dislike", "agree", "disagree", "laugh"].map((reaction, index) => (
          <ReactionCounter
            key={reaction}
            className={index < 4 ? "mr3" : ""}
            reaction={reaction}
            count={getReactionCount(reaction)}
            canControlClassroom={canControlClassroom}
            canFilter={canFilter}
            canEmote={canEmote}
            currentUser={users[currentUserId]}
            handleClick={() => handleClickReaction(reaction)}
          />
        ))}
      </div>
      <div className="flex justify-center">
        {["wow", "love", "clap"].map((reaction) => (
          <ReactionCounter
            key={reaction}
            className="mr3"
            reaction={reaction}
            count={getReactionCount(reaction)}
            canControlClassroom={canControlClassroom}
            canFilter={canFilter}
            canEmote={canEmote}
            currentUser={users[currentUserId]}
            handleClick={() => handleClickReaction(reaction)}
          />
        ))}
        <ReactionCounter
          reaction="hand"
          count={getHandRaiseCount()}
          canControlClassroom={canControlClassroom}
          canFilter={canFilter}
          canEmote={canEmote}
          currentUser={users[currentUserId]}
          handleClick={handleClickHandRaise}
        />
      </div>
    </>
  );
}

export { ReactionCounter, ReactionRollup };
